import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import Watermark from "../components/Watermark";
import { toast } from "react-hot-toast";
import StatusFilterCards from "../components/StatusFilterCards";
import useServerEvents from "../hooks/useServerEvents";
import { getMinDate, validateDueDate } from "../utils/dateUtils";
import { buildOverallWorkbook } from "../utils/exportUtils";

const PRIORITIES = ["Low", "Medium", "High"];

function addBusinessDaysLocal(date, days) {
  let result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    if (result.getDay() !== 0 && result.getDay() !== 6) added++;
  }
  return result;
}
const SORT_OPTIONS = [
  { label: "Task ID Asc", value: "id_asc" },
  { label: "Task ID Desc", value: "id_desc" },
  { label: "Due Date Asc", value: "due_asc" },
  { label: "Due Date Desc", value: "due_desc" },
];

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("Manager");
  const [newTask, setNewTask] = useState({ title: "", description: "", priority: "Low", due_date: "", assigned_to: "", project_id: "" });
  const [managerProjects, setManagerProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(""); // for dashboard filter
  const [projectEmployees, setProjectEmployees] = useState([]);
  const [createMsg, setCreateMsg] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [tasks, setTasks] = useState([]);
  const [sort, setSort] = useState("id_desc");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [taskCreating, setTaskCreating] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [usersMap, setUsersMap] = useState({});
  // ── Overall Export modal state ─────────────────────────────────────────────
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFrom, setExportFrom]           = useState("");
  const [exportTo, setExportTo]               = useState("");
  const [exporting, setExporting]             = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("jwt");
  const currentUserId = (() => {
    try { return JSON.parse(atob((token||'').split('.')[1])).userId; } catch { return null; }
  })();

  // Fetch users map (now returns the map)
  const fetchUsersMap = async () => {
    try {
      const res = await axios.get(`${apiUrl}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const map = {};
      (res.data || []).forEach((u) => {
        map[u.id] = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      });
      setUsersMap(map);
      return map; // return map so caller can use immediately
    } catch (err) {
      return {};
    }
  };

  // Fetch tasks (accept optional usersMap param to avoid stale state)
  const fetchTasks = async (usersMapParam = null) => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // sort by created_at descending (newest first). fallback to id desc
      const getTime = (t) => {
        if (t.created_at) return new Date(t.created_at).getTime();
        return t.id ? Number(t.id) : 0;
      };
      const sorted = (res.data || []).slice().sort((a, b) => getTime(b) - getTime(a));

      const mapToUse = usersMapParam || usersMap || {};
      const withNames = sorted.map((t) => ({
        ...t,
        created_by_name: mapToUse[t.created_by] || null,
      }));

      setTasks(withNames);
    } catch (err) {
      // handle error if needed
    } finally {
      setLoading(false);
    }
  };

  // bootstrap: load users map first, then tasks; keep auto-refresh for tasks only
  useEffect(() => {
    let interval;
    (async () => {
      const map = await fetchUsersMap();
      await fetchTasks(map);
      interval = setInterval(() => fetchTasks(), 30 * 1000);
    })();
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  useServerEvents({
    onTaskCreated: () => fetchTasks(),
    onTaskUpdated: () => fetchTasks(),
  }, token);

  // Fetch manager's projects on mount
  useEffect(() => {
    axios
      .get(`${apiUrl}/projects/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const projects = res.data || [];
        setManagerProjects(projects);
        // Preselect if only one project
        if (projects.length === 1) {
          setNewTask(prev => ({ ...prev, project_id: projects[0].id }));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch employees scoped to the selected project (or all projects if none selected)
  useEffect(() => {
    const params = newTask.project_id ? `?project_id=${newTask.project_id}` : "";
    axios
      .get(`${apiUrl}/projects/my/members${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setProjectEmployees(res.data || []))
      .catch(() => {
        axios
          .get(`${apiUrl}/users`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => setProjectEmployees((r.data || []).filter(u => u.role_id === 1 || u.role_id === '1')))
          .catch(() => {});
      });
    // When project changes, reset the assignee so an invalid combination can't be submitted
    setNewTask(prev => ({ ...prev, assigned_to: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newTask.project_id]);

  const todayMgr = new Date(); todayMgr.setHours(0, 0, 0, 0);
  const parseLocalDate = (d) => { const datePart = String(d).split('T')[0]; const [y, m, day] = datePart.split('-').map(Number); return new Date(y, m - 1, day); };
  // API returns Title Case status names (Open, Ongoing, Blocked, On Hold, Closed)
  // Filter keys use legacy lowercase (open, in_progress, blocked, on_hold, closed)
  const STATUS_MAP = { open: "Open", in_progress: "Ongoing", blocked: "Blocked", on_hold: "On Hold", closed: "Closed" };
  const isNotClosed = (t) => (t.status || "") !== "Closed";

  // Counts always from ALL tasks regardless of tab
  const mgrStats = [
    { label: "Open",      value: tasks.filter(t => t.status === "Open").length,    accent: 'bg-blue-500',   icon: '📋', filter: "open" },
    { label: "Ongoing",   value: tasks.filter(t => t.status === "Ongoing").length, accent: 'bg-yellow-400', icon: '⚡', filter: "in_progress" },
    { label: "Blocked",   value: tasks.filter(t => t.status === "Blocked").length, accent: 'bg-red-500',    icon: '🚫', filter: "blocked" },
    { label: "On Hold",   value: tasks.filter(t => t.status === "On Hold").length, accent: 'bg-orange-400', icon: '⏸', filter: "on_hold" },
    { label: "Closed",    value: tasks.filter(t => t.status === "Closed").length,  accent: 'bg-green-500',  icon: '✅', filter: "closed" },
    { label: "Due Today", value: tasks.filter(t => t.due_date && parseLocalDate(t.due_date).toDateString() === todayMgr.toDateString() && isNotClosed(t)).length, accent: 'bg-amber-400', icon: '📅', filter: "due_today" },
    { label: "Overdue",   value: tasks.filter(t => t.due_date && parseLocalDate(t.due_date) < todayMgr && isNotClosed(t)).length, accent: 'bg-red-700', icon: '⚠️', filter: "overdue" },
  ];

  // Apply status filter across ALL tasks (no tab split — status filter cards handle segmentation)
  let filteredTasks = tasks;
  if (statusFilter === "due_today") {
    filteredTasks = tasks.filter(t => t.due_date && parseLocalDate(t.due_date).toDateString() === todayMgr.toDateString() && isNotClosed(t));
  } else if (statusFilter === "overdue") {
    filteredTasks = tasks.filter(t => t.due_date && parseLocalDate(t.due_date) < todayMgr && isNotClosed(t));
  } else if (statusFilter) {
    const apiStatus = STATUS_MAP[statusFilter];
    filteredTasks = tasks.filter(t => t.status === apiStatus);
  }

  // Search
  const q = (search || "").toString().trim().toLowerCase();
  if (q) {
    filteredTasks = filteredTasks.filter((t) => {
      const idStr = (t.id !== undefined && t.id !== null) ? t.id.toString() : "";
      return (
        idStr.toLowerCase().includes(q) ||
        (t.title || "").toString().toLowerCase().includes(q)
      );
    });
  }

  // Project filter (client-side — tasks are already scoped to manager's projects by the API)
  if (selectedProjectId) {
    filteredTasks = filteredTasks.filter(t => String(t.project_id) === selectedProjectId);
  }

  // Sorting
  if (sort === "id_asc") filteredTasks.sort((a, b) => a.id - b.id);
  if (sort === "id_desc") filteredTasks.sort((a, b) => b.id - a.id);
  if (sort === "due_asc") filteredTasks.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  if (sort === "due_desc") filteredTasks.sort((a, b) => new Date(b.due_date) - new Date(a.due_date));

  // Pagination
  const totalPages = Math.ceil(filteredTasks.length / rowsPerPage);
  const paginatedTasks = filteredTasks.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  
  // Export to Excel — opens date-range modal
  const handleExport = () => {
    setExportFrom("");
    setExportTo("");
    setShowExportModal(true);
  };

  const handleRunExport = async () => {
    if (!exportFrom || !exportTo) {
      toast.error("Please select both a From and To date");
      return;
    }
    if (new Date(exportFrom) > new Date(exportTo)) {
      toast.error('"From" date cannot be after "To" date');
      return;
    }
    setExporting(true);
    try {
      const res = await fetch(
        `${apiUrl}/tasks/export?from=${exportFrom}&to=${exportTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Export failed (${res.status})`);
      }
      const bundle = await res.json();
      if (!bundle.tasks || bundle.tasks.length === 0) {
        toast.error("No tasks found for the selected period");
        setExporting(false);
        return;
      }
      const wb = buildOverallWorkbook(bundle);
      const today = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `Tasks_Export_${exportFrom}_to_${exportTo}_${today}.xlsx`);
      toast.success(`Exported ${bundle.tasks.length} task${bundle.tasks.length !== 1 ? "s" : ""} to Excel`);
      setShowExportModal(false);
    } catch (err) {
      toast.error(err.message || "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Group tasks by month

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.title || !newTask.description) return setCreateMsg("Title and description required");
    // Managers with multiple projects must select one
    if (managerProjects.length > 1 && !newTask.project_id) return setCreateMsg("Please select a project for this task");
    const due_date = newTask.due_date || null;
    const dateErr = validateDueDate(due_date);
    if (dateErr) return setCreateMsg(dateErr);
    if (!newTask.assigned_to) return setCreateMsg("Please select an employee to assign this task to");
    setTaskCreating(true);
    try {
      await axios.post(
        `${apiUrl}/tasks`,
        { ...newTask, due_date, project_id: newTask.project_id || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task created successfully!");
      const defaultProjectId = managerProjects.length === 1 ? managerProjects[0].id : "";
      setNewTask({ title: "", description: "", priority: "Low", due_date: "", assigned_to: "", project_id: defaultProjectId });
      setCreateMsg("");
      fetchTasks();
      setTab("Manager");
    } catch (err) {
      const errMsg = err.response?.data?.error || "Error creating task";
      setCreateMsg(errMsg);
      toast.error(errMsg);
    } finally {
      setTaskCreating(false);
    }
  };

  if (tab === "Create a Task") {
    const priorityDeadlines = { High: "2 business days", Medium: "5 business days", Low: "10 business days" };
    const priorityMeta = {
      High:   { border: "border-red-400",    bg: "bg-red-50",    text: "text-red-700",    dot: "bg-red-400",    desc: "Urgent — due in 2 business days" },
      Medium: { border: "border-yellow-400", bg: "bg-yellow-50", text: "text-yellow-700", dot: "bg-yellow-400", desc: "Standard — due in 5 business days" },
      Low:    { border: "border-[#7AA33E]",  bg: "bg-[#f4f9ec]", text: "text-[#5a8a2a]",  dot: "bg-[#7AA33E]",  desc: "Routine — due in 10 business days" },
    };
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <Watermark />

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">Create a New Task</h1>
              <p className="text-sm text-gray-400 mt-0.5">Fill in the details below to create and assign a project task</p>
            </div>
          </div>
          <button
            disabled={taskCreating}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 disabled:opacity-40 transition"
            onClick={() => { setTab("Manager"); setCreateMsg(""); }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Form Card ── */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm">

            {/* Card Header */}
            <div className="px-8 py-5 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-900">Task Details</h2>
              <p className="text-xs text-gray-400 mt-0.5">Fields marked <span className="text-red-400">*</span> are required</p>
            </div>

            <form onSubmit={handleCreateTask} className="flex flex-col gap-0 px-8 py-6">

              {/* Project selector — only shown when manager has multiple projects */}
              {managerProjects.length > 1 && (
                <>
                  <div className="flex flex-col gap-2 mb-6">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Project <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={newTask.project_id}
                      onChange={e => setNewTask({ ...newTask, project_id: e.target.value, assigned_to: "" })}
                      disabled={taskCreating}
                      className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition appearance-none cursor-pointer"
                      required
                    >
                      <option value="">— Select a project</option>
                      {managerProjects.map(p => (
                        <option key={p.id} value={p.id}>{p.project_name}</option>
                      ))}
                    </select>
                    {!newTask.project_id && (
                      <p className="text-xs text-amber-600">Select a project first — the assignee list will update accordingly.</p>
                    )}
                  </div>
                  <div className="border-t border-gray-100 mb-6" />
                </>
              )}

              {/* Title */}
              <div className="flex flex-col gap-2 mb-6">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Task Title <span className="text-red-400">*</span>
                </label>
                <input
                  name="title"
                  value={newTask.title}
                  onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="e.g. Update quarterly report"
                  disabled={taskCreating}
                  className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition"
                  required
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 mb-6" />

              {/* Description */}
              <div className="flex flex-col gap-2 mb-6">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="description"
                  value={newTask.description}
                  onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Describe what needs to be done, any context or requirements..."
                  disabled={taskCreating}
                  className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white resize-none disabled:opacity-50 transition"
                  rows={5}
                  required
                />
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 mb-6" />

              {/* Priority */}
              <div className="flex flex-col gap-3 mb-6">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {PRIORITIES.map(p => {
                    const m = priorityMeta[p];
                    const active = newTask.priority === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        disabled={taskCreating}
                        onClick={() => setNewTask({ ...newTask, priority: p })}
                        className={`py-3 rounded-xl border-2 text-sm font-semibold transition flex flex-col items-center gap-1 disabled:opacity-50 ${
                          active ? `${m.border} ${m.bg} ${m.text}` : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${active ? m.dot : "bg-gray-300"}`} />
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 mb-6" />

              {/* Assign To */}
              <div className="flex flex-col gap-2 mb-6">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Assign To <span className="text-red-400">*</span>
                  </label>
                  <span className="text-xs text-gray-400">
                    {newTask.project_id
                      ? `${projectEmployees.length} employee${projectEmployees.length !== 1 ? "s" : ""} in ${managerProjects.find(p => String(p.id) === String(newTask.project_id))?.project_name || "project"}`
                      : `${projectEmployees.length} employee${projectEmployees.length !== 1 ? "s" : ""} in project`}
                  </span>
                </div>
                <select
                  value={newTask.assigned_to}
                  onChange={e => setNewTask({ ...newTask, assigned_to: e.target.value })}
                  disabled={taskCreating || (managerProjects.length > 1 && !newTask.project_id)}
                  className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition appearance-none cursor-pointer"
                  required
                >
                  <option value="">— Select an employee</option>
                  {projectEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                  ))}
                </select>
                {projectEmployees.length === 0 && (newTask.project_id || managerProjects.length <= 1) && (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-100">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <p className="text-xs text-amber-700 font-medium">No project employees found. Ask the superuser to add members to this project.</p>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 mb-6" />

              {/* Due Date */}
              <div className="flex flex-col gap-2 mb-6">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</label>
                  <span className="text-xs text-gray-400">Optional — no past dates or Sundays</span>
                </div>
                <input
                  type="date"
                  name="due_date"
                  value={newTask.due_date}
                  min={getMinDate()}
                  disabled={taskCreating}
                  onChange={e => {
                    const val = e.target.value;
                    const err = validateDueDate(val);
                    if (err) { setCreateMsg(err); return; }
                    setCreateMsg("");
                    setNewTask({ ...newTask, due_date: val });
                  }}
                  className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white disabled:opacity-50 transition"
                />
                {!newTask.due_date && (
                  <p className="text-xs text-gray-400">Leave blank to auto-set based on priority ({priorityDeadlines[newTask.priority]})</p>
                )}
              </div>

              {/* Error / validation message */}
              {createMsg && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium mb-5">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  {createMsg}
                </div>
              )}

              {/* Submit footer */}
              <div className="flex gap-3 pt-2 border-t border-gray-100 mt-2">
                <button
                  type="submit"
                  disabled={taskCreating}
                  className="flex-1 py-3 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:bg-[#5d8230] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm shadow-[#7AA33E]/20 flex items-center justify-center gap-2"
                >
                  {taskCreating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating Task…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                      Create Task
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={taskCreating}
                  onClick={() => { setTab("Manager"); setCreateMsg(""); }}
                  className="px-6 py-3 rounded-xl bg-white border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 disabled:opacity-40 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>

          {/* ── Info Sidebar ── */}
          <div className="lg:w-64 flex flex-col gap-4">

            {/* Priority Guide */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Priority Guide</h3>
              <div className="flex flex-col gap-3">
                {Object.entries(priorityMeta).map(([p, m]) => (
                  <div key={p} className="flex items-start gap-3">
                    <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${m.dot}`} />
                    <div>
                      <p className="text-sm font-semibold text-gray-700">{p}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-[#f6faf0] rounded-2xl border border-[#d4e8b0] p-5">
              <h3 className="text-xs font-bold text-[#5a8a2a] uppercase tracking-wider mb-3">Tips</h3>
              <ul className="flex flex-col gap-2.5">
                {[
                  "Be specific in the title — assignees should know what's needed at a glance",
                  "Include links or references in the description",
                  "Leave due date blank to auto-set based on priority",
                  "Only employees in your project can be assigned",
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[#5a7a30]">
                    <span className="mt-0.5 w-1 h-1 rounded-full bg-[#7AA33E] flex-shrink-0 mt-1.5" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {/* Current form preview */}
            {(newTask.title || newTask.assigned_to) && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Preview</h3>
                <div className="flex flex-col gap-2">
                  {newTask.title && (
                    <p className="text-sm font-semibold text-gray-800 truncate">{newTask.title}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priorityMeta[newTask.priority].bg} ${priorityMeta[newTask.priority].text} border ${priorityMeta[newTask.priority].border}`}>
                      {newTask.priority}
                    </span>
                    {newTask.assigned_to && (
                      <span className="text-xs text-gray-500 truncate">
                        → {projectEmployees.find(e => String(e.id) === String(newTask.assigned_to))?.first_name || ""}
                        {" "}
                        {projectEmployees.find(e => String(e.id) === String(newTask.assigned_to))?.last_name || ""}
                      </span>
                    )}
                  </div>
                  {newTask.due_date && (
                    <p className="text-xs text-gray-400">Due {new Date(newTask.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Watermark />

      {/* Header */}
      <div className="flex items-center justify-between pb-6 mb-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-1 h-10 rounded-full bg-[#7AA33E]" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Manager Dashboard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage your project tasks and team assignments</p>
          </div>
        </div>
        <button
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] active:scale-[0.98] transition shadow-sm"
          onClick={() => setTab("Create a Task")}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Create a Task
        </button>
      </div>

      {/* ── Assigned Tasks Card ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-800">All Tasks</h2>
            <p className="text-xs text-gray-400 mt-0.5">{filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} found</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-xl border border-gray-200 bg-gray-50 text-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by Task ID or Title"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="rounded-xl border border-gray-200 bg-gray-50 text-gray-900 pl-9 pr-3 py-2 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white focus:border-transparent transition"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
            >
              <option value="">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="blocked">Blocked</option>
              <option value="on_hold">On Hold</option>
              <option value="closed">Closed</option>
              <option value="due_today">Due Today</option>
              <option value="overdue">Overdue</option>
            </select>
            {/* Project filter — only shown when manager has multiple projects */}
            {managerProjects.length > 1 && (
              <select
                value={selectedProjectId}
                onChange={(e) => { setSelectedProjectId(e.target.value); setPage(1); }}
                className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition appearance-none cursor-pointer"
              >
                <option value="">All Projects</option>
                {managerProjects.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.project_name}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => fetchTasks()}
              title="Refresh"
              className="p-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 hover:bg-white hover:text-[#7AA33E] transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50 transition"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Status Filter Cards */}
        <div className="overflow-x-auto mb-4">
          <StatusFilterCards
            stats={mgrStats}
            activeFilter={statusFilter}
            onFilter={(f) => { setStatusFilter(f); setPage(1); }}
            variant="accent"
          />
        </div>

      {/* Tasks Table — single flat table, all tasks */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full min-w-[1000px] text-sm text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase text-xs tracking-wider">
              <th className="py-3 px-4">#</th>
              <th className="py-3 px-4">Title</th>
              {managerProjects.length > 1 && <th className="py-3 px-4">Project</th>}
              <th className="py-3 px-4">Assigned To</th>
              <th className="py-3 px-4">Priority</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Due Date</th>
              <th className="py-3 px-4">Created</th>
              <th className="py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={managerProjects.length > 1 ? 9 : 8} className="py-16 text-center">
                <div className="flex flex-col items-center gap-3 text-gray-400">
                  <div className="w-8 h-8 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Loading tasks...</span>
                </div>
              </td></tr>
            ) : filteredTasks.length === 0 ? (
              <tr><td colSpan={managerProjects.length > 1 ? 9 : 8} className="py-16 text-center">
                <div className="flex flex-col items-center gap-3 text-gray-400">
                  <svg className="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-sm font-medium text-gray-500">No tasks found</span>
                </div>
              </td></tr>
            ) : (
              paginatedTasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => navigate(`/tasks/${task.id}`)}>
                  <td className="py-3 px-4 text-gray-400 font-mono text-xs">{task.id}</td>
                  <td className="py-3 px-4 font-medium text-gray-800 max-w-[200px] truncate">{task.title}</td>
                  {managerProjects.length > 1 && (
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="px-2 py-0.5 rounded-full bg-[#f0f7e6] text-[#5a8a2a] text-xs font-semibold">
                        {managerProjects.find(p => p.id === task.project_id)?.project_name || <span className="text-gray-400">—</span>}
                      </span>
                    </td>
                  )}
                  <td className="py-3 px-4 text-gray-600 whitespace-nowrap">
                    {task.assignee_name?.trim() || <span className="text-gray-400 italic">Unassigned</span>}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      task.priority === "High"   ? "bg-red-100 text-red-700" :
                      task.priority === "Medium" ? "bg-yellow-100 text-yellow-700" :
                      "bg-green-100 text-green-700"
                    }`}>{task.priority}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                      task.status === "Open"    ? "bg-blue-100 text-blue-700" :
                      task.status === "Ongoing" ? "bg-yellow-100 text-yellow-700" :
                      task.status === "Blocked" ? "bg-red-100 text-red-700" :
                      task.status === "On Hold" ? "bg-orange-100 text-orange-700" :
                      "bg-gray-100 text-gray-500"
                    }`}>{task.status}</span>
                  </td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                    {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                  </td>
                  <td className="py-3 px-4 text-gray-500 whitespace-nowrap text-sm">
                    {task.created_at ? new Date(task.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
                  </td>
                  <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                    <button
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#7AA33E] text-white font-semibold hover:bg-[#6a9335] transition"
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 px-1">
        <div className="text-sm text-gray-600">
          Rows per page:{" "}
          <select
            value={rowsPerPage}
            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
            className="rounded border border-gray-300 bg-white text-gray-700 px-2 py-1 ml-1"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <span>Page {page} of {totalPages || 1}</span>
          <button
            className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition disabled:opacity-40"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >Prev</button>
          <button
            className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition disabled:opacity-40"
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
          >Next</button>
        </div>
      </div>

      </div>{/* end Assigned Tasks Card */}

      {/* ── Overall Export Modal ── */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
            {/* Header */}
            <div className="flex items-center gap-3 px-7 py-5 border-b border-gray-100">
              <div className="w-10 h-10 rounded-xl bg-[#f0f7e6] flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-[#7AA33E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 leading-tight">Export Tasks</h3>
                <p className="text-xs text-gray-400 mt-0.5">Select a date range to export task data to Excel</p>
              </div>
              <button
                className="ml-auto p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-7 py-5 flex flex-col gap-4">
              <p className="text-xs text-gray-500 leading-relaxed">
                Exports all project tasks created within the selected period, including subtasks, comments, attachment metadata, reports, and audit history.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">From</label>
                  <input
                    type="date"
                    value={exportFrom}
                    max={exportTo || undefined}
                    onChange={e => setExportFrom(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">To</label>
                  <input
                    type="date"
                    value={exportTo}
                    min={exportFrom || undefined}
                    onChange={e => setExportTo(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition"
                  />
                </div>
              </div>
              {exportFrom && exportTo && new Date(exportFrom) > new Date(exportTo) && (
                <p className="text-xs text-red-500 font-medium">⚠ "From" date cannot be after "To" date</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-3 px-7 py-5 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                className="flex-1 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-100 transition"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
              >
                Cancel
              </button>
              <button
                disabled={exporting || !exportFrom || !exportTo || new Date(exportFrom) > new Date(exportTo)}
                onClick={handleRunExport}
                className="flex-1 py-2.5 rounded-xl bg-[#7AA33E] text-white font-semibold text-sm hover:bg-[#6a9335] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {exporting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Exporting…</>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default ManagerDashboard;